import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {useDispatch} from "react-redux";
import Utils, {numComparator} from "../../../../../../core/services/utils/utils";
import {setReduxDialogState} from "../../../../../redux/slices/dialogs/actions";
import ReduxStoreRoutes from "../../../../../../core/models/static/routes/redux-store-routes";
import {ReactSortable} from "react-sortablejs";
import {Box, Card, Checkbox, CircularProgress, Fade, FormControlLabel, Skeleton, ToggleButton, toggleButtonClasses, ToggleButtonGroup, Tooltip, Typography} from "@mui/material";
import Paper from "@mui/material/Paper";
import {Add, DeleteOutlined, EditOutlined} from "@mui/icons-material";
import Button from "../../../../../components/base/button/button";
import Grid from "@mui/material/Unstable_Grid2";
import IconButton from "../../../../../components/base/button/icon_button";
import UpsertProductCodeTypeDialog from "../../../../../components/dialogs/upsert-product-code-type-dialog";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import Api from "../../../../../../core/services/api/api";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const initialUpsertDialogState = {
    open: false,
    codeType: null,
    onClose: () => void 0,
    onUpsert: () => void 0,
}

const ProductCodeTypesView = () => {
    const [loading, setLoading] = useState(false);
    const [data, _setData] = useState([]);
    const [removingIds, setRemovingIds] = useState([]);
    const [reordering, setReordering] = useState(false);
    const [upsertCodeTypeDialogState, setUpsertCodeTypeDialogState] = useState(initialUpsertDialogState);
    const isMounted = useIsMounted();
    const dispatch = useDispatch();
    const {configureNotebookTags} = useConfigureNotebook('product-code-types');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.productsCodeTypes);
        configureBehavioralTool((tool) => tool.productsCodeTypes);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Sets the categories state of this component by sorting them based on their orderIndex after the new state is
     * received.
     *
     * This method acts as a wrapper that injects the sorting behaviour at the end of the setting this state.
     */
    const setData = useCallback((stateOrCallback, sort = false) => {
        _setData((prevState) => {
            let newState;
            if (typeof stateOrCallback === 'function') {
                newState = stateOrCallback(prevState)
            } else {
                newState = stateOrCallback
            }
            if (sort) {
                const res = [...newState ?? []];
                res.sort((a, b) => numComparator(a.orderIndex, b.orderIndex));
                return res.map((e, index) => ({...e, orderIndex: index}));
            }
            return newState
        })
    }, [])
    /**
     * Reorders a code-type in the server, and if successful, sets the new state as well.
     */
    const reorderEntry = useCallback(async (e) => {
        const orderedEntries = Utils.reorderList(data, e.oldDraggableIndex, e.newDraggableIndex, 'orderIndex');
        setData(orderedEntries);
        setReordering(true);
        const forApi = {
            id: data[e.oldDraggableIndex].id,
            newIndex: e.newDraggableIndex + 1,
        }
        console.log(forApi)
        // TODO: call api to reorder code type.
        await new Promise(r => setTimeout(r, 1000));
        const response = {
            resultFlag: true,
        }
        if (!isMounted())
            return;
        setReordering(false);
        if (!response?.resultFlag) {
            setData(data);
        }
    }, [data, isMounted, setData])

    /**
     * Fetches the entries in the system.
     */
    const getList = useCallback(async () => {
        setLoading(true);
        const response = await Api.listPartCodeCategories();
        // const response = {
        //     resultFlag: true,
        //     data: Array.from({length: 6}).map((_, index) => ({
        //         id: index,
        //         title: `Code Type ${index + 1}`,
        //         description: `Description for Code Type ${index + 1}`,
        //         shareRuleId: [1, 2, 3][index % 3],
        //         orderIndex: index,
        //         showInClientSide: index % 2 === 0,
        //         isActive: index % 4 === 0,
        //     }))
        // }
        if (!isMounted())
            return;
        setLoading(false);
        if (response?.resultFlag) {
            setData(response.data ?? [], true);
        }
    }, [isMounted, setData]);

    /**
     * As soon as the dialog opens:
     * - fetches the list of entries
     */
    useEffect(() => {
        getList().then();
    }, [getList]);

    /**
     * Removes an entry
     * @param entry the entry to be removed
     */
    const removeEntry = useCallback(async (entry) => {
        dispatch(setReduxDialogState(ReduxStoreRoutes.dialogs.removeConfirmation, {
            open: true,
            message: 'Are you sure you want to remove this code type?',
            data: null,
            removedEntryTitle: `Code Type: "${entry.title}"`,
            onClose: async (confirmed) => {
                if (!confirmed)
                    return;
                setRemovingIds(prevState => [...prevState, entry.id]);
                const response = await Api.deletePartCodeCategory(entry.id);
                if (!isMounted())
                    return false
                setRemovingIds(prevState => prevState.filter(e => e !== entry.id));
                if (response?.resultFlag) {
                    if (response?.data) {
                        // it is de-activated
                        setData(prevState => prevState.map(e => e.id === entry.id ? {...e, isActive: false} : e), true);
                    } else {
                        setData(prevState => prevState.filter(e => e.id !== entry.id), true);
                    }
                }
                return response?.resultFlag ?? false
            }
        }));
    }, [dispatch, isMounted, setData])

    /**
     * Reactivates an entry
     * @type {(function(*): Promise<void>)}
     */
    const reactivateEntry = useCallback(async (entry) => {
        const response = await Api.reActivatePartCodeCategory(entry.id);
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === entry.id ? {...e, isActive: true} : e), true);
        }
    }, [isMounted, setData])

    /**
     * Opens the upsert dialog with the given entry.
     * @type {(function(*=): void)}
     */
    const onUpsertClicked = useCallback((entry = undefined) => {
        setUpsertCodeTypeDialogState({
            ...initialUpsertDialogState,
            open: true,
            codeType: entry,
            onClose: () => setUpsertCodeTypeDialogState(initialUpsertDialogState),
            onUpsert: (codeType) => setData(prevState => {
                const found = prevState.find(e => e.id === codeType.id);
                if (found) {
                    return prevState.map(e => e.id === codeType.id ? {...e, ...codeType} : e)
                }
                return [...prevState, {...codeType, orderIndex: prevState.length}]
            }),
        });
    }, [setData])

    /**
     * Updates the shared rule of an entry
     * @type {(function(*, *): Promise<void>)}
     */
    const updateSharedRule = useCallback(async (entry, shareRuleId) => {
        const response = await Api.updatePartCodeCategory({
            id: entry.id,
            title: entry.title,
            description: entry.description,
            partSharePolicyId: shareRuleId,
            isVisibleInPartInfo: entry.isVisibleInPartInfo,
            updatedProperties: [
                "PartSharePolicyId",
            ]
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === entry.id ? {...e, partSharePolicyId: shareRuleId} : e));
        }
    }, [setData, isMounted])

    /**
     * Toggles the show in client side of an entry.
     * @type {(function(*, *): Promise<void>)}
     */
    const toggleShowInClientSide = useCallback(async (entry, showInClientSide) => {
        const response = await Api.updatePartCodeCategory({
            id: entry.id,
            title: entry.title,
            description: entry.description,
            partSharePolicyId: entry.partSharePolicyId,
            isVisibleInPartInfo: showInClientSide,
            updatedProperties: [
                "IsVisibleInPartInfo",
            ]
        })
        if (!isMounted())
            return;
        if (response?.resultFlag) {
            setData(prevState => prevState.map(e => e.id === entry.id ? {...e, isVisibleInPartInfo: showInClientSide} : e));
        }
    }, [setData, isMounted])

    return (
        <>
            <Box sx={{display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBlockEnd: 4}}>
                <Typography variant={'h6'} fontWeight={600}>
                    Code Types Management
                </Typography>
                <Button
                    disabled={loading}
                    className='d-flex align-items-center justify-content-center'
                    variant={'outlined'}
                    color={'primary'}
                    onClick={() => onUpsertClicked()}
                >
                    <Add className={'mr-2'}/>
                    <p>
                        Add a new Code Type
                    </p>
                </Button>
            </Box>
            <Paper sx={{padding: 2, display: 'flex', flexDirection: 'column'}}>
                {
                    !loading &&
                    <ReactSortable
                        list={data}
                        setList={() => void 0}
                        onUpdate={reorderEntry}
                        handle={'.handle'}
                        ghostClass={'ghost'}
                        animation={210}
                        swapThreshold={0.65}
                        style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 15,
                        }}
                    >
                        {
                            data?.map((entry, array) => (
                                <CodeTypeCard
                                    key={entry.id}
                                    entry={entry}
                                    update={onUpsertClicked}
                                    reactivateEntry={reactivateEntry}
                                    updateSharedRule={updateSharedRule}
                                    toggleShowInClientSide={toggleShowInClientSide}
                                    removing={removingIds.includes(entry.id)}
                                    remove={removeEntry}
                                    removable={array.length > 1}
                                    sortable={!reordering && !removingIds.length}
                                />
                            ))
                        }
                    </ReactSortable>
                }
                {
                    loading &&
                    <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, marginBlock: 1}}>
                        {
                            Array.from({length: 4}).map((_, key) => (
                                <Skeleton
                                    component={'div'}
                                    key={key}
                                    width={'100%'}
                                    height={146}
                                    sx={{transformOrigin: 'none', transform: 'none'}}
                                />
                            ))
                        }
                    </Box>
                }
                {
                    !loading && data.length < 1 &&
                    <Typography variant={'caption'} color={'text.secondary'} textAlign={'center'} sx={{marginBlock: 6, width: '100%'}}>
                        No code types found.
                    </Typography>
                }
            </Paper>
            <UpsertProductCodeTypeDialog {...upsertCodeTypeDialogState}/>
        </>
    );
}

const CodeTypeCard = ({
                          entry,
                          update,
                          updateSharedRule,
                          reactivateEntry,
                          toggleShowInClientSide,
                          remove,
                          removable,
                          removing,
                          sortable
                      }) => {
    const [updatingShareRule, setUpdatingShareRule] = useState(false);
    const [togglingShowInClientSide, setTogglingShowInClientSide] = useState(false);
    const [newShareRuleId, setNewShareRuleId] = useState();
    const [reactivating, setReactivating] = useState(false);
    const isMounted = useIsMounted()

    const toggleButtonOptions = useMemo(() => [
        {
            value: 1,
            title: 'Public',
            description: 'By marking a part as public, all of your partner and customer will have access to all the code types',
        },
        {
            value: 2,
            title: 'Partial Share',
            description: 'The code will be share only for increasing the optimisation of the search engine and will be visible if only the user search for that code',
        },
        {
            value: 3,
            title: 'Private',
            description: 'code will not be shared for user or customer',
        },
    ], []);

    /**
     * Updates the shared rule of the entry.
     * @type {(function(*, *): Promise<void>)}
     */
    const onShareRuleSelected = useCallback(async (e, shareRuleId) => {
        if (!shareRuleId)
            return;
        setNewShareRuleId(shareRuleId);
        setUpdatingShareRule(true);
        await updateSharedRule(entry, shareRuleId);
        if (!isMounted())
            return;
        setUpdatingShareRule(false);
        setNewShareRuleId(null);
    }, [entry, isMounted, updateSharedRule])

    /**
     * Toggles the show in client side of the entry.
     * @type {(function(*): Promise<void>)}
     */
    const onShowInClientSideToggled = useCallback(async (e) => {
        setTogglingShowInClientSide(true);
        await toggleShowInClientSide(entry, e.target.checked);
        if (!isMounted())
            return;
        setTogglingShowInClientSide(false);
    }, [entry, isMounted, toggleShowInClientSide])

    /**
     * Reactivates an entry
     * @type {(function(): Promise<void>)}
     */
    const onReactivateClicked = useCallback(async () => {
        setReactivating(true);
        await reactivateEntry(entry);
        if (!isMounted())
            return;
        setReactivating(false);
    }, [entry, reactivateEntry, isMounted])

    return (
        <>
            <Card variant={'outlined'} sx={{padding: 1}}>
                <Grid container spacing={2} marginInline={0} marginBlock={0}>
                    <Grid xs paddingInline={0}>
                        <Box sx={{display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: 0.5, height: '100%'}}>
                            {/*<Fade in={sortable}>*/}
                            {/*    <IconButton color={'paper.primary'} className={'handle'}>*/}
                            {/*        <DragHandleRounded/>*/}
                            {/*    </IconButton>*/}
                            {/*</Fade>*/}
                            <Typography variant={'body2'} fontWeight={600} {...!entry.isActive && {color: 'text.disabled'}}>
                                {entry.title ?? ""}
                            </Typography>
                            {
                                entry.isActive &&
                                <>
                                    <IconButton
                                        disabled={removing}
                                        onPressed={() => update(entry)}
                                        color={'paper.primary'}
                                    >
                                        <EditOutlined className={'icon normal'}/>
                                    </IconButton>
                                    <Fade in={!removable}>
                                        <IconButton
                                            disabled={removable || removing}
                                            onPressed={() => remove(entry)}
                                            color={'paper.error'}
                                        >
                                            <DeleteOutlined/>
                                        </IconButton>
                                    </Fade>
                                </>
                            }
                        </Box>
                    </Grid>
                    <Grid xs={'auto'}>
                        {
                            entry.isActive &&
                            <ToggleButtonGroup
                                orientation="horizontal"
                                disabled={updatingShareRule || removing}
                                value={newShareRuleId ?? entry.partSharePolicyId}
                                onChange={onShareRuleSelected}
                                exclusive
                                size={'small'}
                                sx={{
                                    [`.${toggleButtonClasses.root}`]: {
                                        fontWeight: 600,
                                        padding: '0.4rem 1rem',
                                    }
                                }}
                            >
                                {
                                    toggleButtonOptions.map((option) => (
                                        <ToggleButton key={option.value} value={option.value}>
                                            <Tooltip title={option.description} disableInteractive>
                                                <Typography
                                                    variant={'body2'}
                                                    fontWeight={600}
                                                    component={'div'}
                                                    sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5}}
                                                >
                                                    {
                                                        newShareRuleId === option.value &&
                                                        <CircularProgress size={16}/>
                                                    }
                                                    {option.title}
                                                </Typography>
                                            </Tooltip>
                                        </ToggleButton>
                                    ))
                                }
                            </ToggleButtonGroup>
                        }
                        {
                            !entry.isActive &&
                            <Button
                                variant={'outlined'}
                                color={'primary'}
                                disabled={removing || reactivating}
                                onClick={onReactivateClicked}
                            >
                                {
                                    reactivating
                                        ? 'Reactivating...'
                                        : "Reactivate"
                                }
                            </Button>
                        }
                    </Grid>
                    <Grid xs={12} sx={{padding: 0,}}/>
                    <Grid xs>
                        <Typography variant={'body2'} {...!entry.isActive && {color: 'text.disabled'}}>
                            {entry.description ?? ""}
                        </Typography>
                    </Grid>
                    <Grid xs={'auto'}>
                        {
                            entry.isActive &&
                            <FormControlLabel
                                label={togglingShowInClientSide ? "Toggling Permission..." : "Show in Client-side"}
                                sx={{margin: 0}}
                                control={
                                    <Checkbox
                                        disabled={removing || togglingShowInClientSide}
                                        sx={{paddingBlock: 0.75}}
                                        disableRipple
                                        checked={entry.isVisibleInPartInfo}
                                        onChange={onShowInClientSideToggled}
                                    />
                                }
                            />
                        }
                    </Grid>
                </Grid>
            </Card>
        </>
    )
}

export default ProductCodeTypesView;
